import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@app/layouts/AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { RoleGuard } from './RoleGuard';
import { LifecycleGuard } from './LifecycleGuard';
import { FullPageLoader } from '@shared/components/LoadingSpinner';
import { ROLES } from '@shared/constants/roles';
import { ROUTES } from '@shared/constants/routes';
import { useSidebarVisibilityStore } from '@shared/store/sidebarVisibilityStore';

// Lazy load feature pages
const DashboardPage = lazy(() => import('@features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const TeamPage = lazy(() => import('@features/team/pages/TeamPage').then(m => ({ default: m.TeamPage })));
const ActivityPage = lazy(() => import('@features/activity/pages/ActivityPage').then(m => ({ default: m.ActivityPage })));
const ResourcesPage = lazy(() => import('@features/resources/pages/ResourcesPage').then(m => ({ default: m.ResourcesPage })));
const DownloadsPage = lazy(() => import('@features/downloads').then(m => ({ default: m.DownloadsPage })));
const SupportPage = lazy(() => import('@features/support/pages/SupportPage').then(m => ({ default: m.SupportPage })));
const ContactSupportPage = lazy(() => import('@features/support/pages/ContactSupportPage').then(m => ({ default: m.ContactSupportPage })));
const NewsletterPage = lazy(() => import('@features/newsletter/pages/NewsletterPage').then(m => ({ default: m.NewsletterPage })));
const DataNetPage = lazy(() => import('@features/datanet/pages/DataNetPage').then(m => ({ default: m.DataNetPage })));
const CheckoutPage = lazy(() => import('@features/checkout/pages/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const ReviewOrderPage = lazy(() => import('@features/checkout/pages/ReviewOrderPage').then(m => ({ default: m.ReviewOrderPage })));
const BillingPage = lazy(() => import('@features/billing/pages/BillingPage').then(m => ({ default: m.BillingPage })));
const SubscriptionsPage = lazy(() => import('@features/subscriptions').then(m => ({ default: m.SubscriptionsPage })));
const RenewalDetailPage = lazy(() => import('@features/subscriptions').then(m => ({ default: m.RenewalDetailPage })));
const QuotesPage = lazy(() => import('@features/quotes').then(m => ({ default: m.QuotesPage })));
const RequestQuotePage = lazy(() => import('@features/quotes').then(m => ({ default: m.RequestQuotePage })));
const SignUpPage = lazy(() => import('@features/auth/pages/SignUpPage').then(m => ({ default: m.SignUpPage })));
const SignUpSuccessPage = lazy(() => import('@features/auth/pages/SignUpSuccessPage').then(m => ({ default: m.SignUpSuccessPage })));
const LogoutPage = lazy(() => import('@features/auth/pages/LogoutPage').then(m => ({ default: m.LogoutPage })));
const UserOnboardPage = lazy(() => import('@features/auth/pages/UserOnboardPage').then(m => ({ default: m.UserOnboardPage })));
const LoginSignupHintPage = lazy(() => import('@features/auth/pages/LoginSignupHintPage').then(m => ({ default: m.LoginSignupHintPage })));

/**
 * Landing after login. Sends dashboard-eligible users to the dashboard;
 * new customers (dashboard not yet unlocked) go straight to checkout so they
 * can buy, instead of seeing an empty home page.
 */
function HomeRedirect() {
  const { lastFetched, error, isItemVisible } = useSidebarVisibilityStore();

  // Wait for the first visibility fetch before deciding where to send the user.
  if (!lastFetched && !error) return <FullPageLoader />;

  if (isItemVisible('dashboard')) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/checkout" replace />;
}

export function AppRouter() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - accessible to all authenticated users once an invoice exists */}
          <Route
            path="/dashboard"
            element={
              <LifecycleGuard section="dashboard">
                <DashboardPage />
              </LifecycleGuard>
            }
          />

          {/* Subscriptions & License - Account Owner, Billing Admin, and License Admin */}
          <Route
            path="/subscriptions"
            element={
              <LifecycleGuard section="subscriptions">
                <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN]}>
                  <SubscriptionsPage />
                </RoleGuard>
              </LifecycleGuard>
            }
          />

          {/* Renewal / Next-Cycle detail — reached from the "Renewal change pending" pill.
              Same guards as /subscriptions. */}
          <Route
            path="/subscriptions/renewal"
            element={
              <LifecycleGuard section="subscriptions">
                <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN]}>
                  <RenewalDetailPage />
                </RoleGuard>
              </LifecycleGuard>
            }
          />
          
          {/* Billing - Account Owner and Billing Admin only */}
          <Route 
            path="/billing"
            element={
              <LifecycleGuard section="invoices">
                <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN]}>
                  <BillingPage />
                </RoleGuard>
              </LifecycleGuard>
            }
          />
          
          {/* Users/Team - All roles can access */}
          <Route 
            path="/team"
            element={
              <LifecycleGuard section="users">
                <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN, ROLES.LICENSE_ADMIN, ROLES.REGISTERED_CONTACT]}>
                  <TeamPage />
                </RoleGuard>
              </LifecycleGuard>
            }
          />
          
          {/* Product Downloads - All roles can access */}
          <Route
            path="/downloads"
            element={
              <LifecycleGuard section="downloads">
                <DownloadsPage />
              </LifecycleGuard>
            }
          />
          
          {/* Other routes - accessible to all authenticated users */}
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          
          {/* Quotes - Account Owner and Billing Admin only.
              LifecycleGuard intentionally removed: the Request Quote flow must be reachable
              before any Halo Quotation exists (the old `quotations` stage flipped visible only
              after Sales created a real quote — chicken-and-egg for the request itself). */}
          <Route
            path="/quotes"
            element={
              <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN]}>
                <QuotesPage />
              </RoleGuard>
            }
          />

          {/* Request Quote form (plan F5) - same gates as /quotes */}
          <Route
            path="/quotes/request"
            element={
              <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN]}>
                <RequestQuotePage />
              </RoleGuard>
            }
          />
          
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact-support" element={<ContactSupportPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          {/* LifecycleGuard is the only gate needed — it reads the same firm_groups-backed
              'datanet' section the sidebar item uses, so the link and the route agree. */}
          <Route
            path="/datanet"
            element={
              <LifecycleGuard section="datanet">
                <DataNetPage />
              </LifecycleGuard>
            }
          />
          {/* Checkout - Account Owner and Billing Admin only, matching the backend's
              [Authorize(Roles = "account-owner,billing-admin")] on SubscriptionController.Checkout. */}
          <Route
            path="/checkout"
            element={
              <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN]}>
                <CheckoutPage />
              </RoleGuard>
            }
          />
          <Route
            path="/checkout/review"
            element={
              <RoleGuard requiredRoles={[ROLES.ACCOUNT_OWNER, ROLES.BILLING_ADMIN]}>
                <ReviewOrderPage />
              </RoleGuard>
            }
          />

          {/* Landing after login (root and any unknown path): dashboard once the
              lifecycle stage allows it, otherwise straight to checkout. No /home. */}
          <Route index element={<HomeRedirect />} />
          <Route path="*" element={<HomeRedirect />} />
        </Route>

        {/* User Onboard - Handles redirect from setup-credentials endpoint
            URL format: /UserOnboard/:username?token=encryptedToken
            Calls login endpoint and stores tokens.
            NOT wrapped in PublicRoute/ProtectedRoute: this is a redirect handler that must
            run even if a stale session exists; it establishes the real session itself. */}
        <Route
          path="/UserOnboard/:username"
          element={<UserOnboardPage />}
        />

        {/* Login-hint deep link - clients send users to
            /login-signup-hint?login-hint=user@example.com
            and we bounce them to Keycloak's login page with the username pre-filled. No wrapper:
            the page itself drives the redirect regardless of current auth state. */}
        <Route
          path="/login-signup-hint"
          element={<LoginSignupHintPage />}
        />

        {/* Public routes */}
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <SignUpPage />
            </PublicRoute>
          }
        />
        <Route
          path="/signup-success"
          element={
            <PublicRoute>
              <SignUpSuccessPage />
            </PublicRoute>
          }
        />
        {/* Post-logout landing page. Must stay OUTSIDE the protected block — ProtectedRoute calls
            keycloak.login() for any unauthenticated visitor, which is exactly what this page
            exists to avoid. PublicRoute sends anyone with a live session back to '/'. */}
        <Route
          path={ROUTES.LOGOUT}
          element={
            <PublicRoute>
              <LogoutPage />
            </PublicRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
